import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { fabric } from 'fabric';
import { PDFDocument } from 'pdf-lib';
import toast from 'react-hot-toast';
import { Type, Pen, Square, Highlighter, Image as ImageIcon, Trash2, MousePointer2, ChevronLeft, ChevronRight } from 'lucide-react';

// Self-hosted PDF.js worker: the matching pdf.worker.min.js is copied into /public,
// so it ships with the app (no external CDN call at runtime). Keep it in sync with the
// installed pdfjs-dist version when upgrading the package.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

// Render scale: how big each PDF page is drawn in the editor. 1.3 keeps text readable
// without blowing up memory on the larger Cosen sheets.
const RENDER_SCALE = 1.3;

// A free-tier PDF editor: it renders the chosen PDF with pdf.js and lays a Fabric.js
// canvas over each page so the user can ADD content — text boxes, freehand drawing,
// highlights, whiteout rectangles and images — then flattens those additions onto the
// original pages with pdf-lib on save. It does NOT edit text already baked into the PDF.
const PdfEditorModal = ({ isOpen, fileBytes, fileName, onCancel, onSave, saving }) => {
  const canvasElRef = useRef(null);       // <canvas> the Fabric overlay binds to
  const fabricRef = useRef(null);         // the fabric.Canvas instance
  const pdfDocRef = useRef(null);         // the pdf.js document
  const pageEditsRef = useRef({});        // { [pageIndex]: { json, w, h } }
  const imageInputRef = useRef(null);
  const sourceBytesRef = useRef(null);    // pristine copy of the original PDF bytes

  const [numPages, setNumPages] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [tool, setTool] = useState('select');
  const [color, setColor] = useState('#e11d48');
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState({ w: 0, h: 0 });

  // Persist the current page's drawings into the in-memory map before leaving it.
  const stashCurrentPage = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const objects = canvas.getObjects();
    if (objects.length > 0) {
      pageEditsRef.current[pageIndex] = {
        json: canvas.toJSON(),
        w: canvas.getWidth(),
        h: canvas.getHeight(),
      };
    } else {
      delete pageEditsRef.current[pageIndex];
    }
  }, [pageIndex]);

  // Draw one PDF page and (re)build the Fabric overlay for it.
  const renderPage = useCallback(async (idx) => {
    const pdf = pdfDocRef.current;
    if (!pdf || !canvasElRef.current) return;
    setLoading(true);
    try {
      const page = await pdf.getPage(idx + 1);
      const viewport = page.getViewport({ scale: RENDER_SCALE });

      // Render the page bitmap to an offscreen canvas.
      const bg = document.createElement('canvas');
      bg.width = Math.floor(viewport.width);
      bg.height = Math.floor(viewport.height);
      await page.render({ canvasContext: bg.getContext('2d'), viewport }).promise;

      // (Re)create the Fabric canvas at the page size with the page bitmap behind it.
      if (fabricRef.current) {
        fabricRef.current.dispose();
        fabricRef.current = null;
      }
      const canvas = new fabric.Canvas(canvasElRef.current, {
        width: bg.width,
        height: bg.height,
        selection: true,
      });
      canvas.setBackgroundImage(bg.toDataURL('image/png'), canvas.renderAll.bind(canvas));
      fabricRef.current = canvas;
      setPageSize({ w: bg.width, h: bg.height });

      // Restore any previous edits for this page.
      const saved = pageEditsRef.current[idx];
      if (saved?.json) {
        canvas.loadFromJSON(saved.json, () => {
          // loadFromJSON wipes the background; re-apply it.
          canvas.setBackgroundImage(bg.toDataURL('image/png'), canvas.renderAll.bind(canvas));
        });
      }
    } catch (err) {
      console.error('Failed to render PDF page:', err);
      toast.error('Could not render this page.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load the document when the modal opens / the file changes.
  useEffect(() => {
    if (!isOpen || !fileBytes) return;
    let cancelled = false;
    pageEditsRef.current = {};
    // pdf.js transfers (detaches) the buffer it loads, so keep a pristine copy for
    // pdf-lib to flatten against on save.
    const original = fileBytes.slice(0);
    sourceBytesRef.current = fileBytes.slice(0);
    (async () => {
      try {
        const pdf = await pdfjsLib.getDocument({ data: original }).promise;
        if (cancelled) return;
        pdfDocRef.current = pdf;
        setNumPages(pdf.numPages);
        setPageIndex(0);
        await renderPage(0);
      } catch (err) {
        console.error('Failed to load PDF:', err);
        toast.error('Could not open this PDF.');
      }
    })();
    return () => {
      cancelled = true;
      if (fabricRef.current) { fabricRef.current.dispose(); fabricRef.current = null; }
      if (pdfDocRef.current) { pdfDocRef.current.destroy?.(); pdfDocRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, fileBytes]);

  // Apply the active tool to the Fabric canvas.
  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas.isDrawingMode = tool === 'draw' || tool === 'highlight';
    if (tool === 'draw') {
      canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
      canvas.freeDrawingBrush.color = color;
      canvas.freeDrawingBrush.width = 2;
    } else if (tool === 'highlight') {
      canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
      canvas.freeDrawingBrush.color = 'rgba(255, 235, 59, 0.4)';
      canvas.freeDrawingBrush.width = 16;
    }
  }, [tool, color, pageSize]);

  const goToPage = async (idx) => {
    if (idx < 0 || idx >= numPages || idx === pageIndex) return;
    stashCurrentPage();
    setPageIndex(idx);
    await renderPage(idx);
  };

  const addText = () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const t = new fabric.IText('Type here', {
      left: 60, top: 60, fontSize: 18, fill: color, fontFamily: 'Arial',
    });
    canvas.add(t);
    canvas.setActiveObject(t);
    t.enterEditing();
    canvas.renderAll();
    setTool('select');
  };

  const addWhiteout = () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    const rect = new fabric.Rect({
      left: 80, top: 80, width: 160, height: 40, fill: '#ffffff', stroke: '#d1d5db', strokeWidth: 1,
    });
    canvas.add(rect);
    canvas.setActiveObject(rect);
    canvas.renderAll();
    setTool('select');
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      fabric.Image.fromURL(ev.target.result, (img) => {
        const canvas = fabricRef.current;
        if (!canvas) return;
        const maxW = canvas.getWidth() * 0.5;
        if (img.width > maxW) img.scaleToWidth(maxW);
        img.set({ left: 80, top: 80 });
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
    setTool('select');
  };

  const deleteSelected = () => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas.getActiveObjects().forEach((o) => canvas.remove(o));
    canvas.discardActiveObject();
    canvas.renderAll();
  };

  // Flatten a single page's overlay JSON to a transparent PNG data URL at page pixels.
  const overlayToPng = (json, w, h) => new Promise((resolve) => {
    const el = document.createElement('canvas');
    const sc = new fabric.StaticCanvas(el, { width: w, height: h });
    sc.loadFromJSON(json, () => {
      sc.renderAll();
      const url = sc.toDataURL({ format: 'png' });
      sc.dispose();
      resolve(url);
    });
  });

  const handleSave = async () => {
    stashCurrentPage();
    const edits = pageEditsRef.current;
    try {
      const pdfDoc = await PDFDocument.load(sourceBytesRef.current);
      const pages = pdfDoc.getPages();
      for (const key of Object.keys(edits)) {
        const i = parseInt(key, 10);
        const { json, w, h } = edits[key];
        if (!json || !pages[i]) continue;
        const pngUrl = await overlayToPng(json, w, h);
        const png = await pdfDoc.embedPng(pngUrl);
        const { width, height } = pages[i].getSize();
        pages[i].drawImage(png, { x: 0, y: 0, width, height });
      }
      const bytes = await pdfDoc.save();
      await onSave(bytes);
    } catch (err) {
      console.error('Failed to export edited PDF:', err);
      toast.error('Could not save the edited PDF.');
    }
  };

  if (!isOpen) return null;

  const ToolButton = ({ id, title, onClick, active, children }) => (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`inline-flex items-center justify-center h-9 w-9 rounded border transition-colors ${
        active ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-base font-semibold text-gray-800 truncate pr-4">Edit brochure — {fileName}</h3>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onCancel} disabled={saving} className="px-4 py-2 text-sm rounded bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50">Cancel</button>
            <button type="button" onClick={handleSave} disabled={saving || loading} className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Use this brochure'}
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 px-4 py-2 border-b bg-gray-50 flex-wrap">
          <ToolButton id="select" title="Select / move" active={tool === 'select'} onClick={() => setTool('select')}><MousePointer2 className="h-4 w-4" /></ToolButton>
          <ToolButton id="text" title="Add text" onClick={addText}><Type className="h-4 w-4" /></ToolButton>
          <ToolButton id="draw" title="Draw" active={tool === 'draw'} onClick={() => setTool('draw')}><Pen className="h-4 w-4" /></ToolButton>
          <ToolButton id="highlight" title="Highlight" active={tool === 'highlight'} onClick={() => setTool('highlight')}><Highlighter className="h-4 w-4" /></ToolButton>
          <ToolButton id="whiteout" title="Whiteout box" onClick={addWhiteout}><Square className="h-4 w-4" /></ToolButton>
          <ToolButton id="image" title="Add image" onClick={() => imageInputRef.current?.click()}><ImageIcon className="h-4 w-4" /></ToolButton>
          <ToolButton id="delete" title="Delete selected" onClick={deleteSelected}><Trash2 className="h-4 w-4" /></ToolButton>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} title="Color" className="h-9 w-9 rounded border border-gray-300 cursor-pointer bg-white p-0.5" />
          <input ref={imageInputRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" />

          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={() => goToPage(pageIndex - 1)} disabled={pageIndex <= 0} className="inline-flex items-center justify-center h-9 w-9 rounded border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
            <span className="text-sm text-gray-600">Page {numPages ? pageIndex + 1 : 0} of {numPages}</span>
            <button type="button" onClick={() => goToPage(pageIndex + 1)} disabled={pageIndex >= numPages - 1} className="inline-flex items-center justify-center h-9 w-9 rounded border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>

        {/* Canvas area */}
        <div className="flex-1 overflow-auto bg-gray-200 flex items-start justify-center p-4 relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm">Loading…</div>
          )}
          <div className="shadow-lg" style={{ width: pageSize.w, height: pageSize.h }}>
            <canvas ref={canvasElRef} />
          </div>
        </div>

        <div className="px-4 py-2 border-t text-xs text-gray-500">
          You can add text, drawings, highlights, whiteout boxes and images. Editing the PDF's existing printed text is not supported.
        </div>
      </div>
    </div>
  );
};

export default PdfEditorModal;
